"use client";
import React from "react";
import { Caveat } from "next/font/google";
import { motion } from "framer-motion";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

/* ─────────────────────────────────────────────────────────────
   SPA RESORT: SEIZE EVERY OPPORTUNITY SECTION
   Exact reproduction of Odoo's Website Contact Form to CRM Lead Pipeline UI
   ───────────────────────────────────────────────────────────── */
export function SpaSeizeOpportunitySection({ section }) {
  const title = section?.title || "Seize every opportunity";
  const subtext = section?.subtext || "Effortlessly turn website visitors into leads with contact form submissions, generate leads automatically via email alias, or create them manually.";

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">


        {/* SECTION HEADER WITH HANDWRITTEN TITLE & BLUE HAND CLICK DOODLE */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10 sm:mb-14 text-center max-w-4xl mx-auto space-y-4"
        >
          <h2 className={`${caveat.className} text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight inline-flex flex-wrap items-center justify-center gap-2 sm:gap-4 relative`}>
            <span>Seize</span>

            {/* Blue Oval Circle around "every" */}
            <span className="relative inline-block px-2">
              every
              <svg
                className="absolute -inset-2 w-[125%] h-[140%] text-[#00A09D] overflow-visible -left-2 -top-1.5"
                viewBox="0 0 120 50"
                fill="none"
              >
                <ellipse
                  cx="60"
                  cy="25"
                  rx="54"
                  ry="20"
                  stroke="#00A09D"
                  strokeWidth="4"
                  fill="none"
                />
              </svg>
            </span>

            <span>opportunity</span>

            {/* Blue Hand Click Doodle Pointer on top right */}
            <div className="absolute -top-10 -right-8 sm:-top-12 sm:-right-12 select-none transform rotate-12">
              <svg width="48" height="48" viewBox="0 0 64 64" fill="none" className="w-9 h-9 sm:w-12 sm:h-12">
                {/* Hand icon */}
                <path
                  d="M 32 12 C 30 12, 28 14, 28 18 L 28 32 C 26 30, 23 28, 20 28 C 17 28, 15 30, 15 33 C 15 42, 24 50, 34 50 C 44 50, 48 44, 48 34 L 48 24 C 48 21, 46 19, 43 19 C 41 19, 39 20, 38 22 L 38 18 C 38 15, 36 12, 32 12 Z"
                  fill="#00A09D"
                  stroke="#0077C5"
                  strokeWidth="3"
                />
                {/* Click ripples */}
                <circle cx="32" cy="8" r="4" stroke="#00A09D" strokeWidth="2.5" fill="none" />
                <path d="M 22 10 A 14 14 0 0 1 42 10" stroke="#00A09D" strokeWidth="2.5" strokeLinecap="round" fill="none" />
              </svg>
            </div>
          </h2>

          <p className="text-xl text-gray-300 leading-relaxed">
            <strong className="text-white font-bold">Effortlessly turn website visitors into leads</strong> with contact form submissions, generate leads automatically via email alias, or create them manually.
          </p>
        </motion.div>

        {/* OVERLAPPING MOCKUPS: CONTACT FORM & CRM KANBAN BOARD */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="grid lg:grid-cols-12 gap-6 items-center"
        >

          {/* LEFT MOCKUP: WEBSITE CONTACT US FORM (7 COLS) */}
          <div className="lg:col-span-5 bg-white  shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-xs w-full">
            
            {/* Dark Header Banner */}
            <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white p-3 sm:p-5 border-b border-slate-700">
              <h3 className="text-xl sm:text-2xl font-bold font-sans tracking-tight">Contact us</h3>
            </div>

            {/* Form & Company Contact Info Body */}
            <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-12 gap-5">
              
              {/* Form Fields Column (8 cols) */}
              <div className="md:col-span-8 space-y-2 text-[11px]">
                <p className="text-slate-500 leading-tight">
                  Contact us about anything related to our company or services.<br />
                  We&apos;ll do our best to get back to you as soon as possible.
                </p>

                <div className="space-y-2 pt-1">
                  <div>
                    <label className="block text-slate-600 font-medium mb-1 text-[10px]">Name *</label>
                    <input
                      type="text"
                      readOnly
                      defaultValue="Justin Dotremon"
                      className="w-full bg-slate-50 border border-slate-200 px-2 py-1 text-slate-800 font-medium focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 font-medium mb-1 text-[10px]">Phone Number</label>
                    <input
                      type="text"
                      readOnly
                      defaultValue="+321234567890"
                      className="w-full bg-slate-50 border border-slate-200 px-2 py-1 text-slate-800 font-medium focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 font-medium mb-1 text-[10px]">Email *</label>
                    <input
                      type="text"
                      readOnly
                      defaultValue="Jdotr@example.com"
                      className="w-full bg-slate-50 border border-slate-200 px-2 py-1 text-slate-800 font-medium focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 font-medium mb-1 text-[10px]">Company</label>
                    <input
                      type="text"
                      readOnly
                      defaultValue=""
                      className="w-full bg-slate-50 border border-slate-200 px-2 py-1 text-slate-800 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 font-medium mb-1 text-[10px]">Subject *</label>
                    <input
                      type="text"
                      readOnly
                      defaultValue="Coaching session"
                      className="w-full bg-slate-50 border border-slate-200 px-2 py-1 text-slate-800 font-medium focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 font-medium mb-1 text-[10px]">Question *</label>
                    <textarea
                      readOnly
                      rows={2}
                      defaultValue="Hello,&#10;Are you taking on new clients?"
                      className="w-full bg-slate-50 border border-slate-200 px-2 py-1 text-slate-800 font-medium focus:outline-none resize-none"
                    />
                  </div>

                  <div className="pt-1">
                    <button className="bg-pink-100 text-pink-700 hover:bg-pink-200 font-semibold px-4 py-1.5 text-[11px] transition-colors shadow-sm">
                      Submit
                    </button>
                  </div>
                </div>
              </div>

              {/* Company Info Column (4 cols) */}
              <div className="md:col-span-4 space-y-3 text-[10px] text-slate-600 border-t md:border-t-0 md:border-l border-slate-100 pt-3 md:pt-0 md:pl-4">
                <strong className="block text-slate-800 font-bold text-xs">My Company</strong>
                <div className="space-y-2 text-slate-600">
                  <div className="flex items-start gap-1.5">
                    <span>📍</span>
                    <span>3575 Fake Buena Vista Avenue</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span>📞</span>
                    <span>+1 555-555-5556</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span>✉️</span>
                    <span className="text-sky-600 truncate">info@yourcompany.example.com</span>
                  </div>
                </div>
              </div>

            </div>

          </div>

          {/* MIDDLE CONNECTING ARROW */}
          <div className="hidden lg:flex lg:col-span-1 justify-center items-center">
            <span className="text-3xl text-purple-400 font-bold animate-pulse">➔</span>
          </div>

          {/* RIGHT MOCKUP: ODOO CRM LEADS KANBAN BOARD (4 COLS) */}
          <div className="lg:col-span-4 bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-xs w-full relative">
            
            {/* Top Right Sparkle Rays */}
            <div className="absolute -top-3 -right-3 text-cyan-400 select-none z-20">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <line x1="20" y1="4" x2="20" y2="12" stroke="#00A09D" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="32" y1="10" x2="25" y2="16" stroke="#00A09D" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="36" y1="22" x2="28" y2="22" stroke="#00A09D" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>

            {/* Odoo CRM Header Bar */}
            <div className="bg-slate-100 border-b border-slate-200 px-3 py-2 flex items-center justify-between gap-2 text-[10px]">
              <div className="flex items-center gap-2 font-medium text-slate-700">
                <span className="font-bold text-slate-900 flex items-center gap-1">
                  <span className="text-teal-600">▲</span> CRM
                </span>
                <span className="hover:text-slate-900 cursor-pointer">Sales</span>
                <span className="hover:text-slate-900 cursor-pointer">Reporting</span>
                <span className="hover:text-slate-900 cursor-pointer">Configuration</span>
              </div>
            </div>

            {/* Action Bar */}
            <div className="p-2.5 bg-white border-b border-slate-100 flex items-center justify-between gap-2 text-[10px]">
              <div className="flex items-center gap-1.5">
                <button className="bg-purple-700 text-white px-2.5 py-1 rounded font-semibold text-[10px]">
                  New
                </button>
                <span className="text-slate-600 font-medium">Generate Leads</span>
              </div>
              <span className="text-slate-500 font-medium">Pipeline ⚙</span>
            </div>

            {/* Kanban Board Column */}
            <div className="p-3 bg-slate-50/60 space-y-3 min-h-[280px]">
              
              {/* Column Header */}
              <div className="flex items-center justify-between border-b border-slate-200 pb-1.5 text-[11px]">
                <div className="flex items-center gap-1.5 font-bold text-slate-800">
                  <span>New</span>
                  <span className="text-slate-400 font-normal text-[10px]">+</span>
                </div>
                <span className="text-slate-400 font-bold text-[10px]">0</span>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden">
                <div className="bg-emerald-500 h-full w-3/4 rounded-full" />
              </div>

              {/* Kanban Card 1: Coaching / Justin Dotremon */}
              <div className="bg-white rounded-lg p-2.5 shadow-sm border border-slate-200 space-y-1 hover:shadow-md transition-shadow">
                <h5 className="font-bold text-slate-900 text-xs">Coaching</h5>
                <span className="text-[10px] text-slate-500 block">Justin Dotremon</span>
                
                <div className="flex items-center justify-between pt-1 text-[10px]">
                  <div className="flex items-center gap-0.5 text-amber-400 text-xs">
                    <span>★</span>
                    <span>★</span>
                    <span className="text-slate-300">★</span>
                    <span className="text-slate-400 text-[10px] ml-1">🕒</span>
                  </div>
                  <span className="w-5 h-5 rounded bg-purple-700 text-white flex items-center justify-center font-bold text-[10px]">
                    J
                  </span>
                </div>
              </div>

              {/* Kanban Card 2: Sports Injury Rehabilitation / Daniel Takahashi */}
              <div className="bg-white rounded-lg p-2.5 shadow-sm border border-slate-200 space-y-1 hover:shadow-md transition-shadow">
                <h5 className="font-bold text-slate-900 text-xs">Sports Injury Rehabilitation</h5>
                <span className="text-[10px] text-slate-500 block">Daniel Takahashi</span>
                
                <div className="flex items-center justify-between pt-1 text-[10px]">
                  <div className="flex items-center gap-0.5 text-amber-400 text-xs">
                    <span>★</span>
                    <span>★</span>
                    <span className="text-slate-300">★</span>
                    <span className="text-slate-400 text-[10px] ml-1">🕒</span>
                  </div>
                  <span className="w-5 h-5 rounded bg-emerald-600 text-white flex items-center justify-center font-bold text-[10px]">
                    J
                  </span>
                </div>
              </div>

            </div>

          </div>

        </motion.div>

      </div>
    </section>
  );
}

export function SpaMakeMostOfYourTimeSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* TOP PERSONA SPEECH BUBBLE CALLOUT */}
        <div className="w-full flex justify-center mb-10 sm:mb-14">
          <div className="flex items-center justify-center gap-2 sm:gap-3 max-w-full px-2">
            <img
              className="rounded-full border border-white/20 shadow-lg flex-shrink-0 w-11 h-11 sm:w-14 sm:h-14 object-cover"
              alt="Odoo Specialist"
              src="/odoo/snippets/s_wd_persona/christina.webp"
            />
            <div className="bg-white/10 text-slate-800 rounded-full shadow-2xl p-2.5 sm:p-3 px-5 sm:px-8 border border-slate-100 text-center">
              <i className="text-white text-xs sm:text-base font-semibold block leading-snug">
                Effortlessly manage your patient information.
              </i>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start">

          {/* LEFT COLUMN: TITLE & COPYWRITING */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 space-y-6 sm:space-y-8 text-left relative"
          >
            {/* Pink Clock Icon on Top Right of Title */}
            <div className="hidden sm:block absolute -top-4 right-0 text-rose-400 select-none">
              <div className="w-12 h-12 rounded-full border-2 border-rose-400 bg-rose-100 flex items-center justify-center text-rose-600 font-bold shadow-md">
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="9" />
                  <polyline points="12 7 12 12 15 15" />
                </svg>
              </div>
            </div>

            {/* Title with Pink Dotted Line Under "time" */}
            <h2 className={`${caveat.className} text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1]`}>
              <div>
                Make the most of your{" "}
                <span className="relative inline-block px-1">
                  time
                  <svg
                    className="absolute -bottom-2 left-0 w-full h-3 text-rose-400 overflow-visible"
                    viewBox="0 0 100 12"
                    fill="none"
                  >
                    <path
                      d="M 4 6 Q 50 2, 96 6"
                      stroke="#FF6B6B"
                      strokeWidth="6"
                      strokeDasharray="2 8"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </div>
            </h2>

            {/* Subtext Copy */}
            <div className="space-y-5">
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Optimize time management.</strong> Define availability options for appointments and let your clients select their favorite slot. This streamlines scheduling, reduces back-and-forth communication, and boosts customer satisfaction.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Calendar alarms or reminders.</strong> Automate notifications, emails, or SMS reminders to reduce no-shows.
              </p>
            </div>
          </motion.div>



          {/* RIGHT COLUMN: OVERLAPPING APPOINTMENT CATALOG & MOBILE CALENDAR MOCKUPS */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 relative w-full"
          >
            {/* OVERLAPPING WRAPPER CONTAINER */}
            <div className="relative space-y-4">

              {/* TOP RIGHT MOBILE APPOINTMENT CALENDAR PICKER CARD */}
              <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 text-slate-800 text-xs w-full max-w-xs ml-auto transform md:rotate-1 hover:rotate-0 transition-transform duration-300 z-20 relative">
                
                {/* Navbar */}
                <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-2 text-[10px]">
                  <span className="font-bold text-slate-900 flex items-center gap-1">
                    <span className="text-purple-600">❖</span>
                  </span>
                  <span className="text-slate-500">≡</span>
                </div>

                <div className="text-[10px] text-slate-500 mb-1">← All Appointments</div>
                <h4 className="font-bold text-slate-900 text-xs mb-3">Select a date &amp; time</h4>

                {/* Calendar Widget Header */}
                <div className="bg-slate-50 rounded-lg p-2 border border-slate-100 mb-3">
                  <div className="flex items-center justify-between font-bold text-[11px] text-slate-800 mb-2">
                    <span>November 2023</span>
                    <div className="flex items-center gap-1 text-slate-400">
                      <span>‹</span>
                      <span>›</span>
                    </div>
                  </div>

                  {/* Days grid */}
                  <div className="grid grid-cols-7 text-center text-[9px] text-slate-400 gap-1 font-semibold">
                    <span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span>
                    <span className="text-slate-300">29</span><span className="text-slate-300">30</span><span className="text-slate-300">31</span><span>1</span><span>2</span><span>3</span><span>4</span>
                    <span>5</span><span className="text-rose-600 font-bold underline">6</span><span className="w-5 h-5 rounded-full bg-purple-900 text-white font-bold flex items-center justify-center mx-auto">7</span><span>8</span><span>9</span><span>10</span><span>11</span>
                    <span>12</span><span>13</span><span>14</span><span>15</span><span>16</span><span>17</span><span>18</span>
                    <span>19</span><span>20</span><span>21</span><span>22</span><span>23</span><span>24</span><span>25</span>
                  </div>
                </div>

                {/* Time Slot Buttons */}
                <div className="space-y-1.5 text-[10px]">
                  <button className="w-full py-1.5 border border-slate-200 rounded font-semibold text-slate-700 hover:bg-slate-50 text-center">
                    10:00 AM
                  </button>
                  <button className="w-full py-1.5 border border-slate-200 rounded font-semibold text-slate-700 hover:bg-slate-50 text-center">
                    11:30 AM
                  </button>
                </div>

              </div>

              {/* CONNECTING CURVED ARROW */}
              <div className="hidden sm:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-purple-400 select-none z-30">
                <svg width="60" height="40" viewBox="0 0 60 40" fill="none">
                  <path d="M 10 30 Q 30 5, 50 20" stroke="#8B5CF6" strokeWidth="3" strokeLinecap="round" fill="none" />
                  <path d="M 42 12 L 52 20 L 40 24" fill="#8B5CF6" />
                </svg>
              </div>

              {/* BOTTOM DESKTOP APPOINTMENT SERVICE CATALOG CARD */}
              <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 sm:p-6 text-slate-800 text-xs w-full sm:-mt-24 relative z-10">
                
                {/* Navbar */}
                <div className="flex flex-wrap items-center justify-between border-b border-slate-100 pb-3 mb-4 gap-2 text-[11px]">
                  <div className="flex items-center gap-3 text-slate-600 font-medium">
                    <span className="font-bold text-slate-900">❖</span>
                    <span className="hover:text-slate-900 cursor-pointer">Home</span>
                    <span className="hover:text-slate-900 cursor-pointer">Services</span>
                    <span className="hover:text-slate-900 cursor-pointer">About us</span>
                    <span className="font-bold text-slate-900">Appointment</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-500 text-[10px]">
                    <span>+1 (650) 555-0111</span>
                    <span className="font-semibold text-slate-800">Mitchell Admin ▼</span>
                  </div>
                </div>

                {/* Section Header */}
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-base sm:text-lg font-bold text-slate-900">Choose your appointment</h4>
                  <input
                    type="text"
                    readOnly
                    placeholder="Search..."
                    className="bg-slate-50 border border-slate-200 px-3 py-1 rounded text-[10px] w-32 sm:w-44"
                  />
                </div>

                {/* Services Grid (4 Cards) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-[10px]">
                  
                  {/* Service Card 1 */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50/50 hover:shadow-md transition-shadow">
                    <div className="h-24 bg-slate-200 relative overflow-hidden">
                      <img src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=400&q=80" alt="Dental Care" className="w-full h-full object-cover" />
                    </div>
                    <div className="p-2.5 space-y-1">
                      <h5 className="font-bold text-slate-900 text-xs">Dental Care</h5>
                      <span className="text-[9px] text-slate-500 block">📍 Doctor&apos;s Office</span>
                      <p className="text-[9px] text-slate-500 leading-tight">
                        Prioritize your oral health by scheduling an appointment with our experienced dental professionals.
                      </p>
                    </div>
                  </div>

                  {/* Service Card 2 */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50/50 hover:shadow-md transition-shadow">
                    <div className="h-24 bg-slate-200 relative overflow-hidden">
                      <img src="https://images.unsplash.com/photo-1595435934249-5df7ed86e1c0?auto=format&fit=crop&w=400&q=80" alt="Tennis Court" className="w-full h-full object-cover" />
                    </div>
                    <div className="p-2.5 space-y-1">
                      <h5 className="font-bold text-slate-900 text-xs">Tennis Court</h5>
                      <span className="text-[9px] text-slate-500 block">📍 Tennis Club</span>
                      <p className="text-[9px] text-slate-500 leading-tight">
                        Whether you&apos;re seasoned player or a beginner looking to rally, our easy-to-use platform ensures you can secure the perfect court.
                      </p>
                    </div>
                  </div>

                  {/* Service Card 3 */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50/50 hover:shadow-md transition-shadow">
                    <div className="h-24 bg-slate-200 relative overflow-hidden">
                      <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=400&q=80" alt="Interviews availabilities" className="w-full h-full object-cover" />
                    </div>
                    <div className="p-2.5 space-y-1">
                      <h5 className="font-bold text-slate-900 text-xs">Interviews availabilities</h5>
                      <span className="text-[9px] text-slate-500 block">🎥 Online</span>
                    </div>
                  </div>

                  {/* Service Card 4 */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50/50 hover:shadow-md transition-shadow">
                    <div className="h-24 bg-slate-200 relative overflow-hidden">
                      <img src="https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&w=400&q=80" alt="Online Cooking Lesson" className="w-full h-full object-cover" />
                    </div>
                    <div className="p-2.5 space-y-1">
                      <h5 className="font-bold text-slate-900 text-xs">Online Cooking Lesson</h5>
                      <strong className="text-slate-800 block text-[10px]">$ 150.00</strong>
                      <span className="text-[9px] text-slate-500 block">🎥 Online</span>
                      <p className="text-[9px] text-slate-500 leading-tight">
                        Discover the secrets kept in high-end kitchens with one of our starred chefs...
                      </p>
                    </div>
                  </div>

                </div>

              </div>

            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   3. SPA RESORT: GET PAID IN A FLASH SECTION
   Exact reproduction of Odoo's Checkout Payment & Order Summary UI
   ───────────────────────────────────────────────────────────── */
export function SpaGetPaidInAFlashSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* TOP PERSONA SPEECH BUBBLE CALLOUT */}
        <div className="w-full flex justify-center mb-10 sm:mb-14">
          <div className="flex items-center justify-center gap-2 sm:gap-3 max-w-full px-2">
            <img
              className="rounded-full border border-white/20 shadow-lg flex-shrink-0 w-11 h-11 sm:w-14 sm:h-14 object-cover"
              alt="Odoo Specialist"
              src="/odoo/snippets/s_wd_persona/bob.webp"
            />
            <div className="bg-white/10 text-slate-800 rounded-full shadow-2xl p-2.5 sm:p-3 px-5 sm:px-8 border border-slate-100 text-center">
              <i className="text-white text-xs sm:text-base font-semibold block leading-snug">
                Stop chasing, start earning.
              </i>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">

          {/* LEFT COLUMN: ORDER SUMMARY / CHECKOUT PAYMENT MOCKUP CARD */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 relative w-full flex justify-center lg:justify-start"
          >
            {/* Background Arc Accent */}
            <div className="absolute -left-12 -top-12 -bottom-12 w-4/5 bg-slate-100/10 rounded-[60px] pointer-events-none -z-10 hidden lg:block" />

            {/* White Checkout Card */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-100 text-slate-800 text-[11px] w-full max-w-[310px] p-4 sm:p-5 space-y-3 font-sans relative">
              
              {/* Accordion Header */}
              <div className="flex items-start justify-between border-b border-slate-100 pb-2.5">
                <div>
                  <h4 className="font-bold text-slate-900 text-xs sm:text-sm">Order summary</h4>
                  <span className="text-[10px] text-slate-500 font-sans block mt-0.5">1 item(s) - $ 172.50</span>
                </div>
                <svg className="w-3.5 h-3.5 text-slate-500 mt-0.5 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Price Breakdown */}
              <div className="space-y-1.5 text-[10px] border-b border-slate-100 pb-2.5">
                <div className="flex justify-between text-slate-500">
                  <span>Subtotal</span>
                  <span className="font-semibold text-slate-800">$ 150.00</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>Taxes</span>
                  <span className="font-semibold text-slate-800">$ 22.50</span>
                </div>
              </div>

              {/* Total Calculation */}
              <div className="flex justify-between text-slate-900 font-bold text-xs sm:text-sm border-b border-slate-100 pb-2.5">
                <span>Total</span>
                <span>$ 172.50</span>
              </div>

              {/* Promo Code Input & Apply Button */}
              <div className="flex items-center gap-1.5 pt-0.5">
                <input
                  type="text"
                  readOnly
                  placeholder="Gift card or discount code..."
                  className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-[10px] text-slate-700 w-full focus:outline-none placeholder:text-slate-400 shadow-sm"
                />
                <button className="bg-[#1E293B] text-white font-medium px-3.5 py-1.5 rounded-lg text-[10px] hover:bg-slate-900 transition-colors shadow-sm flex-shrink-0">
                  Apply
                </button>
              </div>

              {/* Primary Pay Now Button */}
              <div className="pt-1">
                <button className="w-full bg-[#6B4C63] hover:bg-[#583D51] text-white font-semibold py-2.5 rounded-lg text-xs transition-colors shadow-md text-center">
                  Pay now
                </button>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-2 text-slate-400 text-[10px] py-0.5">
                <div className="h-px bg-slate-100 flex-1" />
                <span className="text-slate-400 text-[9px]">or</span>
                <div className="h-px bg-slate-100 flex-1" />
              </div>

              {/* Back to delivery link */}
              <div className="text-center pt-0.5">
                <span className="text-[#6B4C63] hover:underline font-medium text-[10px] cursor-pointer inline-flex items-center gap-1">
                  ‹ Back to delivery
                </span>
              </div>

            </div>


          </motion.div>

          {/* RIGHT COLUMN: HANDWRITTEN TITLE & COPYWRITING WITH LIGHTNING DOODLES */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 space-y-6 sm:space-y-8 text-left relative"
          >
            {/* Title with Yellow "flash" & Lightning Bolts */}
            <h2 className={`${caveat.className} text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1] relative inline-block`}>
              <span>Get paid in a </span>
              <span className="text-[#F1BF38] relative inline-block px-1">
                flash
                
                {/* Left Lightning Bolt Doodle */}
                <span className="absolute -top-6 -left-6 sm:-top-8 sm:-left-8 text-[#F1BF38] select-none transform -rotate-12">
                  <svg width="36" height="44" viewBox="0 0 36 44" fill="none">
                    <path d="M 22 2 L 6 22 L 18 24 L 12 42 L 32 18 L 18 16 Z" fill="#F1BF38" stroke="#D97706" strokeWidth="2" />
                  </svg>
                </span>

                {/* Right Lightning Bolt Doodle */}
                <span className="absolute -top-8 -right-8 sm:-top-10 sm:-right-10 text-[#F1BF38] select-none transform rotate-12">
                  <svg width="40" height="48" viewBox="0 0 40 48" fill="none">
                    <path d="M 24 2 L 8 24 L 20 26 L 14 46 L 36 20 L 22 18 Z" fill="#F1BF38" stroke="#D97706" strokeWidth="2" />
                  </svg>
                </span>
              </span>
            </h2>

            {/* Subtext Paragraph */}
            <p className="text-xl text-gray-300 leading-relaxed">
              <strong className="text-white font-bold">Add online up-front payments</strong> when booking appointments to ensure steady cash flow and focus on delivering your services without financial worries. Clients can pay using their preferred online payment method - whatever it may be.
            </p>
          </motion.div>


        </div>

      </div>
    </section>
  );
}


